import { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Paper,
  Typography,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Gavel, Security, Description, CheckCircle, HealthAndSafety } from '@mui/icons-material';
import { useTranslation, Trans } from 'react-i18next';
import { LanguageSwitcher } from '../common/LanguageSwitcher';

// Bump both TERMS_VERSION here and the matching hardcoded 'v1.1' sent by PatientRegister.tsx's
// handleTermsAccept whenever this content changes materially — they are two separate literals by
// design (this one is display-only; PatientRegister's is what's actually recorded), but must stay
// in sync so what a patient sees on-screen matches what's on record as accepted.
// The 2026-10-15 "Linking your records at other ClinVea clinics" section (medopd-docs 137) describes an optional feature
// with its own versioned notice and consent (pl-link-2026-10-15), so it does not bump this version or force every patient
// to re-accept — whether counsel agrees is an open item in medopd-docs 137.
const TERMS_VERSION = 'v1.1';
const EFFECTIVE_DATE = 'September 19, 2026';

interface PatientTermsProps {
  onAccept: (ipAddress: string, userAgent: string) => void;
  onCancel: () => void;
}

export function PatientTermsAndConditions({ onAccept, onCancel }: PatientTermsProps) {
  const { t } = useTranslation('portal');
  const [accepted, setAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showFullTerms, setShowFullTerms] = useState(false);

  const handleAccept = async () => {
    if (!accepted || !privacyAccepted) {
      return;
    }

    // Collect consent metadata
    const userAgent = navigator.userAgent;

    // IP is derived server-side from the HTTP request (X-Forwarded-For / RemoteAddr)
    // Do not call external services for IP — unreliable, rate-limited, and a privacy concern
    const ipAddress = 'server-derived';

    onAccept(ipAddress, userAgent);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        padding: { xs: 2, md: 4 },
        paddingTop: { xs: 4, md: 6 },
      }}
    >
      <Container maxWidth="lg">
        <Paper elevation={3} sx={{ padding: { xs: 3, md: 5 }, marginBottom: 4 }}>
          {/* Language choice up front — this is the first screen a Telugu-only reader meets, so
              the choice must be visible outright rather than behind a settings menu. */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 1 }}>
            <LanguageSwitcher variant="inline" />
          </Box>

          {/* Header */}
          <Box sx={{ textAlign: 'center', marginBottom: 4 }}>
            <Box
              component="img"
              sx={{
                maxWidth: '140px',
                height: 'auto',
                marginBottom: 2,
              }}
            />
            <Typography variant="h4" sx={{ fontWeight: 800, marginBottom: 1 }}>
              {t('patientPortalAgreement')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('termsOfServiceAndPrivacyPolicy')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', marginTop: 1 }}>
              <Trans
                i18nKey="versionEffectiveDate"
                ns="portal"
                values={{ termsVersion: TERMS_VERSION, effectiveDate: EFFECTIVE_DATE }}
              />
            </Typography>
          </Box>

          <Divider sx={{ marginBottom: 3 }} />

          {/* Key Highlights */}
          <Box sx={{ marginBottom: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, marginBottom: 2, display: 'flex', alignItems: 'center' }}>
              <CheckCircle sx={{ marginRight: 1, color: 'primary.main' }} />
              {t('yourRightsAndOurCommitment')}
            </Typography>
            <Alert severity="info" sx={{ marginBottom: 2 }}>
              {t('yourHealthInformationIsProtectedUnder')}
            </Alert>

            <List>
              <ListItem>
                <ListItemText
                  primary={t('yourHealthDataIsProtectedHeading')}
                  secondary={t('allMedicalRecordsAreSecuredUnder')}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t('accessYourMedicalRecordsAnytimeHeading')}
                  secondary={t('viewPrescriptionsLabReportsAndAppointment')}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t('confidentialityGuaranteedHeading')}
                  secondary={t('yourInformationIsSharedOnlyWith')}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t('youControlYourDataHeading')}
                  secondary={t('requestAccessCorrectionOrDeletionOf')}
                />
              </ListItem>
            </List>
          </Box>

          {/* Terms Summary */}
          <Paper elevation={0} sx={{ padding: 3, backgroundColor: 'rgba(0,0,0,0.02)', marginBottom: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, marginBottom: 2, display: 'flex', alignItems: 'center' }}>
              <Gavel sx={{ marginRight: 1, color: 'warning.main' }} />
              {t('patientRightsAndResponsibilities')}
            </Typography>
            
            <Typography variant="subtitle2" sx={{ fontWeight: 700, marginTop: 2, marginBottom: 1 }}>
              {t('n1RegulatoryFramework')}
            </Typography>
            <Typography variant="body2" paragraph>
              <Trans t={t} i18nKey="termsComplianceWithDpdp" components={{ 0: <strong /> }} /><br />
              <Trans t={t} i18nKey="termsAdherenceToCea" components={{ 0: <strong /> }} /><br />
              <Trans t={t} i18nKey="termsConformityWithImc" components={{ 0: <strong /> }} /><br />
              <Trans t={t} i18nKey="termsProtectionUnderCpa" components={{ 0: <strong /> }} />
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, marginTop: 2, marginBottom: 1 }}>
              {t('n2PatientPortalServices')}
            </Typography>
            <Typography variant="body2" paragraph>
              {t('viewAndDownloadDigitalPrescriptionsAnd')}<br />
              {t('bookRescheduleOrCancelAppointmentsWith')}<br />
              {t('accessLabReportsAndDiagnosticResults')}<br />
              {t('communicateWithHealthcareProvidersThroughSecure')}<br />
              {t('requestPrescriptionRefillsAndPharmacyOrders')}<br />
              {t('updatePersonalAndContactInformation')}<br />
              {t('manageAppointmentRemindersAndNotifications')}<br />
              {t('viewAiGeneratedSummariesOfYourHealth')}<br />
              {t('linkAndViewAFamilyMembers')}<br />
              {t('controlWhichClinicsCanView')}
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, marginTop: 2, marginBottom: 1 }}>
              {t('n3YourRightsAsAPatient')}
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>{t('rightToAccess')}</strong> {t('viewAllYourMedicalRecordsPrescriptions')}<br />
              <strong>{t('rightToCorrection')}</strong> {t('requestUpdatesToIncorrectOrIncomplete')}<br />
              <strong>{t('rightToDataPortability')}</strong> {t('downloadYourMedicalRecordsInStandard')}<br />
              <strong>{t('rightToErasure')}</strong> {t('requestDeletionOfYourDataSubject')}<br />
              <strong>{t('rightToPrivacy')}</strong> {t('controlWhoCanAccessYourHealth')}<br />
              <strong>{t('rightToGrievanceRedressal')}</strong> {t('fileComplaintsAboutDataHandlingOr')}<br />
              <strong>{t('rightToQualityCare')}</strong> {t('receiveAppropriateMedicalCareWithoutDiscrimination')}
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, marginTop: 2, marginBottom: 1 }}>
              {t('n4PatientResponsibilities')}
            </Typography>
            <Typography variant="body2" paragraph>
              {t('provideAccurateAndCompleteMedicalHistory')}<br />
              {t('keepLoginCredentialsConfidentialAndSecure')}<br />
              {t('attendScheduledAppointmentsOrCancelWith')}<br />
              {t('followPrescribedTreatmentPlansAndMedical')}<br />
              {t('informHealthcareProvidersOfAnyAllergies')}<br />
              {t('reportAnySecurityConcernsOrUnauthorized')}<br />
              {t('respectHealthcareProvidersTimeAndProfessional')}
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, marginTop: 2, marginBottom: 1 }}>
              {t('n5MedicalInformationAccuracy')}
            </Typography>
            <Typography variant="body2" paragraph>
              {t('allPrescriptionsAndMedicalRecordsAre')}<br />
              {t('platformDisplaysInformationAsRecordedBy')}<br />
              {t('anyQuestionsAboutMedicalContentShould')}<br />
              {t('aiGeneratedSummariesAndExplanationsAre')}<br />
              {t('platformIsNotLiableForMedical')}<br />
              {t('inCaseOfMedicalEmergencyImmediately')}
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, marginTop: 2, marginBottom: 1 }}>
              {t('n6AppointmentManagement')}
            </Typography>
            <Typography variant="body2" paragraph>
              {t('bookAppointmentsBasedOnDoctorAvailability')}<br />
              {t('receiveRemindersViaEmailSmsOr')}<br />
              {t('cancelOrRescheduleAtLeast2')}<br />
              {t('repeatedNoShowsMayResultIn')}<br />
              {t('consultationFeesAreDeterminedByHealthcare')}<br />
              {t('platformFacilitationFeesIfApplicableWill')}
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, marginTop: 2, marginBottom: 1 }}>
              {t('n7ProhibitedActivities')}
            </Typography>
            <Typography variant="body2" paragraph>
              {t('sharingLoginCredentialsWithOthers')}<br />
              {t('misrepresentingMedicalHistoryOrSymptoms')}<br />
              {t('usingPlatformForFraudulentPrescriptionsOr')}<br />
              {t('harassingOrThreateningHealthcareProviders')}<br />
              {t('attemptingToAccessOtherPatientsRecords')}<br />
              {t('automatedScrapingOrDataExtractionFrom')}<br />
              {t('resellingOrCommercialUseOfPlatform')}
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, marginTop: 2, marginBottom: 1 }}>
              {t('n8AccountSecurity')}
            </Typography>
            <Typography variant="body2" paragraph>
              {t('youAreResponsibleForMaintainingPassword')}<br />
              {t('useStrongPasswordsMinimum8Characters')}<br />
              {t('logOutAfterEachSessionOn')}<br />
              {t('reportLostStolenDevicesWithPortal')}<br />
              {t('platformWillNeverAskForPassword')}<br />
              {t('suspiciousLoginAttemptsWillTriggerSecurity')}
            </Typography>
          </Paper>

          {/* Privacy Highlights */}
          <Paper elevation={0} sx={{ padding: 3, backgroundColor: 'rgba(59, 130, 246, 0.05)', marginBottom: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, marginBottom: 2, display: 'flex', alignItems: 'center' }}>
              <Security sx={{ marginRight: 1, color: 'info.main' }} />
              {t('healthDataProtectionAndPrivacy')}
            </Typography>
            
            <Typography variant="body2" paragraph>
              <strong>{t('healthInformationWeCollect')}</strong>
            </Typography>
            <Typography variant="body2" paragraph>
              {t('personalDetailsNameDateOfBirth')}<br />
              {t('medicalHistoryDiagnosesSymptomsAllergiesChronic')}<br />
              {t('treatmentRecordsPrescriptionsLabTestsProcedures')}<br />
              {t('appointmentDataVisitHistoryProviderNotes')}<br />
              {t('technicalDataDeviceInformationIpAddress')}<br />
              {t('paymentInformationTransactionHistoryForBilling')}
            </Typography>

            <Typography variant="body2" paragraph>
              <strong>{t('howWeUseYourHealthData')}</strong>
            </Typography>
            <Typography variant="body2" paragraph>
              {t('facilitateMedicalConsultationsAndTreatmentContinuity')}<br />
              {t('generatePrescriptionsAndLabReports')}<br />
              {t('appointmentSchedulingAndReminders')}<br />
              {t('billingAndInsuranceProcessing')}<br />
              {t('qualityImprovementAndPatientSafetyMonitoring')}<br />
              {t('complianceWithLegalAndRegulatoryRequirements')}<br />
              {t('researchOnlyWithExplicitConsentAnd')}
            </Typography>

            <Typography variant="body2" paragraph>
              <strong>{t('whoCanAccessYourHealthInformation')}</strong>
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>{t('directAccess')}</strong><br />
              {t('yourTreatingDoctorsAndAuthorizedHealthcare')}<br />
              {t('pharmacistsFulfillingYourPrescriptionsMedicationList')}<br />
              {t('youFullAccessToYourOwn')}<br />
              <br />
              <strong>{t('limitedAccessWithYourConsent')}</strong><br />
              {t('familyMembersYouExplicitlyAuthorize')}<br />
              {t('aParentGuardianViewingALinked')}<br />
              {t('otherClinveaClinicsYouExplicitly')}<br />
              {t('insuranceProvidersForClaimProcessing')}<br />
              {t('specialistsForReferralPurposes')}<br />
              {t('otherHealthcareProvidersWhenYourDoctor')}<br />
              <br />
              <strong>{t('legalDisclosure')}</strong><br />
              {t('healthAuthoritiesDuringDiseaseOutbreakInvestigations')}<br />
              {t('lawEnforcementWithValidCourtOrder')}<br />
              {t('medicalEmergenciesWhereConsentCannotBe')}
            </Typography>

            {/* medopd-docs 137 — linking records at other ClinVea clinics. LEGAL REVIEW PENDING (wording not yet reviewed). */}
            <Typography variant="body2" paragraph>
              <strong>{t('privacyLinkingTitle', 'Linking your records at other ClinVea clinics')}</strong>
            </Typography>
            <Typography variant="body2" paragraph>
              {t('privacyLinkingWhat')}<br />
              {t('privacyLinkingHow')}<br />
              {t('privacyLinkingPurpose')}<br />
              {t('privacyLinkingWithdraw')}
            </Typography>

            <Typography variant="body2" paragraph>
              <strong>{t('dataSecurityMeasures')}</strong>
            </Typography>
            <Typography variant="body2" paragraph>
              • <strong>{t('encryption')}</strong> {t('aes256EncryptionAtRestTls')}<br />
              • <strong>{t('accessControls')}</strong> {t('roleBasedPermissionsAndMultiFactor')}<br />
              • <strong>{t('auditTrails')}</strong> {t('completeLoggingOfWhoAccessedYour')}<br />
              • <strong>{t('dataCenters')}</strong> {t('hostedInIndiaWithIso27001')}<br />
              • <strong>{t('backupAndRecovery')}</strong> {t('dailyEncryptedBackupsWithDisasterRecovery')}<br />
              • <strong>{t('incidentResponse')}</strong> {t('breachNotificationWithin72HoursAs')}<br />
              {t('whatsappRemindersAreDeliveredVia')}
            </Typography>

            <Typography variant="body2" paragraph>
              <strong>{t('dataRetention')}</strong>
            </Typography>
            <Typography variant="body2" paragraph>
              {t('medicalRecordsRetainedForMinimum5')}<br />
              {t('prescriptions3YearsMinimumPharmacyRegulations')}<br />
              {t('appointmentHistory2Years')}<br />
              {t('inactiveAccountsDataRetainedFor1')}<br />
              {t('youCanRequestDataDeletionAfter')}
            </Typography>

            <Typography variant="body2" paragraph>
              <strong>{t('yourPrivacyControls')}</strong>
            </Typography>
            <Typography variant="body2" paragraph>
              {t('chooseWhichFamilyMembersCanView')}<br />
              {t('optOutOfResearchDataUsage')}<br />
              {t('blockSharingWithSpecificHealthcareProviders')}<br />
              {t('controlNotificationPreferencesEmailSmsApp')}<br />
              {t('requestDetailedAccessLogsShowingWho')}<br />
              {t('downloadCompleteMedicalHistoryInPortable')}
            </Typography>
          </Paper>

          {/* Special Protections */}
          <Paper elevation={0} sx={{ padding: 3, backgroundColor: 'rgba(16, 185, 129, 0.05)', marginBottom: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, marginBottom: 2, display: 'flex', alignItems: 'center' }}>
              <HealthAndSafety sx={{ marginRight: 1, color: 'success.main' }} />
              {t('specialProtectionsForSensitiveHealthInformation')}
            </Typography>
            
            <Typography variant="body2" paragraph>
              <strong>{t('extraProtectionFor')}</strong>
            </Typography>
            <Typography variant="body2" paragraph>
              {t('mentalHealthRecordsAndPsychiatricTreatment')}<br />
              {t('hivAidsStatusAndTreatment')}<br />
              {t('substanceAbuseTreatmentRecords')}<br />
              {t('reproductiveHealthAndFamilyPlanning')}<br />
              {t('geneticTestingAndCounseling')}<br />
              {t('sexualHealthConsultations')}
            </Typography>

            <Typography variant="body2" paragraph>
              <strong>{t('theseRecordsRequireYourExplicitWritten')}</strong>
            </Typography>
            <Typography variant="body2" paragraph>
              {t('otherHealthcareProviders')}<br />
              {t('insuranceCompanies')}<br />
              {t('familyMembers')}<br />
              {t('legalAuthoritiesExceptValidCourtOrders')}
            </Typography>
          </Paper>

          {/* Grievance & Support */}
          <Paper elevation={0} sx={{ padding: 3, backgroundColor: 'rgba(0,0,0,0.02)', marginBottom: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, marginBottom: 1, display: 'flex', alignItems: 'center' }}>
              <Description sx={{ marginRight: 1, fontSize: 20 }} />
              {t('supportAndGrievanceRedressal')}
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>{t('patientSupportTechnicalIssuesPrivacyConcerns')}</strong> {t('supportClinveaComAvailable9Am')}<br />
              <strong>{t('medicalEmergencies')}</strong> {t('dial102108Immediately')}
            </Typography>
            
            <Typography variant="body2" paragraph>
              <strong>{t('complaintEscalation')}</strong>
            </Typography>
            <Typography variant="body2">
              {t('n1EmailGrievanceOfficerResponseWithin')}<br />
              {t('n2IfUnresolvedEscalateToState')}<br />
              {t('n3ConsumerCourtUnderConsumerProtection')}<br />
              {t('n4DataProtectionBoardForPrivacy')}
            </Typography>
          </Paper>

          {/* Important Disclaimers */}
          <Alert severity="warning" sx={{ marginBottom: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, marginBottom: 1 }}>
              {t('importantMedicalDisclaimers')}
            </Typography>
            <Typography variant="body2">
              {t('thisPlatformIsForConsultationAnd')}<br />
              {t('alwaysSeekImmediateMedicalAttentionFor')}<br />
              {t('platformDoesNotProvideMedicalAdvice')}<br />
              {t('telemedicineHasLimitationsInPersonConsultation')}<br />
              {t('youAreResponsibleForFollowingMedical')}
            </Typography>
          </Alert>

          {/* Acceptance Checkboxes */}
          <Paper elevation={0} sx={{ padding: 3, backgroundColor: 'rgba(59, 130, 246, 0.05)', border: '2px solid rgba(59, 130, 246, 0.3)' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, marginBottom: 2 }}>
              {t('patientConsentAndAgreement')}
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  <Trans t={t} i18nKey="termsAgreeToTermsOfService" components={{ 0: <strong /> }} />
                </Typography>
              }
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  <Trans t={t} i18nKey="termsConsentToPrivacyPolicy" components={{ 0: <strong /> }} />
                </Typography>
              }
            />

            <Alert severity="info" sx={{ marginTop: 2 }}>
              <Typography variant="body2">
                <strong>{t('electronicConsent')}</strong> {t('byClickingAcceptAndContinueYou')}
              </Typography>
            </Alert>
          </Paper>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, marginTop: 3, justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={onCancel}
              sx={{ paddingX: 4 }}
            >
              {t('decline')}
            </Button>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={() => setShowFullTerms(true)}
              >
                {t('viewCompleteTerms')}
              </Button>
              <Button
                variant="contained"
                onClick={handleAccept}
                disabled={!accepted || !privacyAccepted}
                sx={{ paddingX: 4, fontWeight: 700 }}
              >
                {t('acceptAndContinueToRegistration')}
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Footer */}
        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', paddingBottom: 2 }}>
          {t('n2026ClinveaPatientPortalAllRights')}
        </Typography>
      </Container>

      {/* Full Terms Dialog */}
      <Dialog
        open={showFullTerms}
        onClose={() => setShowFullTerms(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('completePatientAgreement')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            {t('theCompleteTermsAndConditionsDisplayed')}
          </Typography>
          <Typography variant="body2" component="div">
            {t('digitalPersonalDataProtectionAct20232')}<br />
            {t('clinicalEstablishmentsAct2010')}<br />
            {t('consumerProtectionAct20192')}<br />
            {t('indianMedicalCouncilRegulations')}
          </Typography>
          <Alert severity="info" sx={{ marginTop: 2 }}>
            {t('forQuestionsAboutTheseTermsContact')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFullTerms(false)}>{t('close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
